/**
 * x-announcement-bar directive.
 *
 * Manages the visibility of the announcement bar using a cookie. The bar is
 * shown by default and hidden for a configurable number of days after the
 * user closes it. In Shopify preview/design mode the bar is always visible
 * regardless of the cookie state.
 *
 * @options
 *   cookieName    - Cookie key used to track the closed state (default: "announcement_bar_closed").
 *   cookieDays    - Number of days to suppress the bar after closing (default: 7).
 *   isPreviewMode - When true, always shows the bar and never writes the cookie (default: false).
 *
 * @example
 *   <div x-announcement-bar="{ cookieDays: 14 }">…</div>
 */
(() => {
  const COOKIE_NAME = "announcement_bar_closed";
  /** @type {number} Number of days to hide the bar after closing. */
  const COOKIE_DAYS = 7;

  const root = (Alpine, el, options = {}) => {
    const {
      cookieName = COOKIE_NAME,
      cookieDays = COOKIE_DAYS,
      isPreviewMode = false,
    } = options;

    Alpine.bind(el, () => ({
      ["x-show"]: "visible",

      ["x-data"]() {
        return {
          visible: false,

          /**
           * Initialises visibility by checking the closed cookie.
           * Always shows the bar in preview/design mode.
           */
          init() {
            this.visible = isPreviewMode || !this.$cookie.get(cookieName);
          },

          /**
           * Hides the bar and sets the closed cookie for the configured duration.
           * Does not write the cookie when in preview mode.
           */
          close() {
            if (!isPreviewMode) {
              this.$cookie.set(cookieName, "true", {
                expires: cookieDays,
                path: "/",
                sameSite: "Lax",
              });
            }

            this.visible = false;
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("announcement-bar", (el, { expression }, { evaluate }) => {
      root(Alpine, el, expression ? evaluate(expression) : {});
    });
  });
})();
